import { useEffect, useRef, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { useFocusTrap } from '../../../lib/a11y/useFocusTrap';
import { useEscClose } from '../../../lib/a11y/useEscClose';
import { Scrollable } from '../../../components/primitives/Scrollable';
import { shouldDismissSheet } from './sheetDismiss';
import styles from './BottomSheet.module.css';

/** Live pointer state for an in-progress grabber drag. */
interface DragState {
  pointerId: number;
  startY: number;
  lastY: number;
  lastTime: number;
  /** px/ms from the most recent pointermove; positive = downward. */
  velocity: number;
}

interface Props {
  children: ReactNode;
  /**
   * Invoked when the user dismisses the sheet via Escape. The visible
   * close affordance lives in the children (e.g. VendorSnapshot's close
   * button) and should call back to the same handler.
   */
  onClose?: () => void;
  /**
   * Accessible name for the dialog. Falls back to a generic label so
   * screen readers always announce something useful when the sheet opens.
   */
  ariaLabel?: string;
  /**
   * If a child element with this id renders the dialog's title (e.g. the
   * vendor's name), wire it as `aria-labelledby` instead of `aria-label`.
   */
  ariaLabelledBy?: string;
  /**
   * Called with the sheet's root DOM node on mount, and `null` on unmount.
   * AppShell uses this to feed a live ResizeObserver for the map's
   * bottom inset (so bottom-of-map markers can pan above the sheet).
   */
  onSheetMount?: (node: HTMLDivElement | null) => void;
  /**
   * Id of an element inside the sheet that describes its content (e.g. the
   * vendor description paragraph). Wired as `aria-describedby` on the dialog.
   */
  ariaDescribedBy?: string;
}

export function BottomSheet({
  children,
  onClose,
  ariaLabel,
  ariaLabelledBy,
  onSheetMount,
  ariaDescribedBy,
}: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  // Focus trap: keeps Tab inside the sheet while it's open and restores
  // focus to whatever was focused before opening (typically the marker
  // that was tapped — which is fine; on the map that's the body, which
  // is also fine).
  useFocusTrap(true, sheetRef);

  // Escape closes when a handler is provided. No-op if the parent didn't
  // pass one — preserves prior behavior for any callers that haven't
  // opted into ESC dismissal.
  useEscClose(Boolean(onClose), () => onClose?.());

  // Only set aria-labelledby if both a label is provided AND no labelledby
  // is provided (mutually exclusive per ARIA semantics).
  const labelProps =
    ariaLabelledBy != null
      ? { 'aria-labelledby': ariaLabelledBy }
      : { 'aria-label': ariaLabel ?? 'Vendor details' };

  // Defensive: ensure the sheet container itself can receive focus as a
  // fallback target before useFocusTrap finds the first focusable inside.
  // Also forward the actual sheet node to the caller for measurement.
  useEffect(() => {
    const node = sheetRef.current;
    if (node && !node.hasAttribute('tabindex')) {
      node.setAttribute('tabindex', '-1');
    }
    onSheetMount?.(node);
    return () => onSheetMount?.(null);
  }, [onSheetMount]);

  // --- Swipe-down dismiss ---------------------------------------------
  // The recognizer is scoped to the grabber only (NOT the scrollable body,
  // which keeps its native scroll). During the drag the sheet follows the
  // finger downward via a direct inline transform (no React re-renders);
  // on release we either dismiss through the existing onClose or settle
  // back to rest. Threshold math lives in sheetDismiss.ts.

  /** Reset inline drag styles, optionally animating the sheet back to rest. */
  const settleBack = (node: HTMLDivElement, offset: number) => {
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (offset <= 0 || reduceMotion) {
      // Nothing to animate (or the user asked us not to): snap instantly.
      node.style.transition = '';
      node.style.transform = '';
      return;
    }
    node.style.transition = 'transform 0.25s cubic-bezier(0.2, 0.9, 0.2, 1)';
    node.style.transform = '';
    node.addEventListener(
      'transitionend',
      () => {
        node.style.transition = '';
      },
      { once: true },
    );
  };

  const handleGrabberPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!onClose || !e.isPrimary || e.button !== 0) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      lastY: e.clientY,
      lastTime: e.timeStamp,
      velocity: 0,
    };
    // Route subsequent moves to the grabber even when the finger strays off
    // it. Feature-checked: jsdom doesn't implement pointer capture.
    if (typeof e.currentTarget.setPointerCapture === 'function') {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    // Follow the finger 1:1 — no transition (and no lingering entry
    // animation) while dragging.
    sheet.style.animation = 'none';
    sheet.style.transition = 'none';
  };

  const handleGrabberPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const sheet = sheetRef.current;
    if (!drag || !sheet || e.pointerId !== drag.pointerId) return;
    const dt = e.timeStamp - drag.lastTime;
    if (dt > 0) drag.velocity = (e.clientY - drag.lastY) / dt;
    drag.lastY = e.clientY;
    drag.lastTime = e.timeStamp;
    // Only translate downward — upward drags pin the sheet at rest.
    const offset = Math.max(0, e.clientY - drag.startY);
    sheet.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
  };

  const handleGrabberPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const sheet = sheetRef.current;
    if (!drag || !sheet || e.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    const offset = Math.max(0, e.clientY - drag.startY);
    if (
      shouldDismissSheet({
        offset,
        sheetHeight: sheet.offsetHeight,
        velocity: drag.velocity,
      })
    ) {
      // Clear inline styles first in case the parent keeps the sheet
      // mounted after onClose (ours unmounts it, but don't assume).
      sheet.style.transition = '';
      sheet.style.transform = '';
      onClose?.();
      return;
    }
    settleBack(sheet, offset);
  };

  const handleGrabberPointerCancel = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const sheet = sheetRef.current;
    if (!drag || !sheet || e.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    settleBack(sheet, Math.max(0, e.clientY - drag.startY));
  };

  return (
    <div
      ref={sheetRef}
      className={styles.sheet}
      role="dialog"
      aria-modal="true"
      {...labelProps}
      {...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy })}
    >
      {/* Decorative drag affordance; keyboard/SR users dismiss via the close
          button or ESC, so the grabber stays out of the a11y tree. */}
      <div
        className={styles.grabber}
        aria-hidden="true"
        data-testid="sheet-grabber"
        onPointerDown={handleGrabberPointerDown}
        onPointerMove={handleGrabberPointerMove}
        onPointerUp={handleGrabberPointerUp}
        onPointerCancel={handleGrabberPointerCancel}
      >
        <div className={styles.grabberBar} />
      </div>
      <Scrollable className={styles.body}>{children}</Scrollable>
    </div>
  );
}
